import { Badge } from "@bowfast/ui/components/badge";
import { Button } from "@bowfast/ui/components/button";
import { Card, CardContent } from "@bowfast/ui/components/card";
import { ScrollArea } from "@bowfast/ui/components/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@bowfast/ui/components/sheet";
import { Skeleton } from "@bowfast/ui/components/skeleton";
import { Textarea } from "@bowfast/ui/components/textarea";
import { cn } from "@bowfast/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { motion } from "framer-motion";
import {
  ArrowLeftIcon,
  CheckIcon,
  CompassIcon,
  DownloadIcon,
  FileTextIcon,
  LayersIcon,
  MapPinIcon,
  Navigation2Icon,
  SailboatIcon,
  UploadIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { BowfastMap } from "~/components/bowfast-map";
import { OverlayPassageStats } from "~/components/routes/OverlayPassageStats";
import { OverlayWaypointList } from "~/components/routes/OverlayWaypointList";
import { fadeUp } from "~/lib/animations";
import { createSdk } from "~/lib/gql";
import { LEGEND_ITEMS, TYPE_CONFIG } from "./routes.config";
import { toRouteDetailView } from "./routes.utils";

// ── RouteDetailPage ───────────────────────────────────────────────────────────

export function RouteDetailPage() {
  const { routeId } = useParams({ strict: false }) as { routeId: string };
  const auth = useAuth();

  const { data, isPending, isError } = useQuery({
    queryKey: ["route-detail", routeId],
    queryFn: () => createSdk(auth.user?.access_token ?? "").RouteDetail({ id: routeId }),
    enabled: !!auth.user?.access_token,
    staleTime: 60 * 1000,
  });

  const route = useMemo(() => (data?.route ? toRouteDetailView(data.route) : null), [data]);

  const mapWaypoints = useMemo(
    () =>
      route?.waypoints.map((wp) => ({
        name: wp.name,
        // OverlayWaypointList stores coords as [lng, lat] on the raw object —
        // BowfastMap needs [lng, lat] pairs from mapRoute; we match by index.
        coords: route.mapRoute[route.waypoints.indexOf(wp)]! as [number, number],
        kind: wp.kind,
      })),
    [route],
  );

  const [seamarkOn, setSeamarkOn] = useState(true);
  const [routeOn, setRouteOn] = useState(true);
  const [notes, setNotes] = useState("");

  if (isError) {
    return (
      <div className="flex h-[calc(100vh-3.5rem)] items-center justify-center">
        <div className="text-center space-y-3">
          <p className="text-sm text-destructive font-medium">Failed to load route</p>
          <Button variant="outline" size="sm" nativeButton={false} render={<Link to="/routes" />}>
            <ArrowLeftIcon data-icon="inline-start" />
            Back to routes
          </Button>
        </div>
      </div>
    );
  }

  if (!isPending && !route) {
    return (
      <div className="flex h-[calc(100vh-3.5rem)] items-center justify-center">
        <div className="text-center space-y-3">
          <p className="text-sm text-muted-foreground">Route not found</p>
          <Button variant="outline" size="sm" nativeButton={false} render={<Link to="/routes" />}>
            <ArrowLeftIcon data-icon="inline-start" />
            Back to routes
          </Button>
        </div>
      </div>
    );
  }

  const typeCfg = route ? TYPE_CONFIG[route.type] : null;

  return (
    <div className="mx-auto flex h-[calc(100vh-3.5rem)] w-full max-w-[1400px] flex-col gap-3 p-4 md:p-6">
      {/* ── Page header ── */}
      <motion.div {...fadeUp} className="space-y-4">
        {/* Top row: back + title + actions */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex gap-3">
            <Button
              variant="ghost"
              size="sm"
              className="mt-0.5 size-9 p-0"
              nativeButton={false}
              render={<Link to="/routes" />}
            >
              <ArrowLeftIcon className="size-4" />
            </Button>
            <div className="max-w-2xl">
              {isPending ? (
                <div className="space-y-2">
                  <Skeleton className="h-6 w-64" />
                  <Skeleton className="h-4 w-80" />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2.5">
                    <h1 className="font-heading text-xl font-bold tracking-tight md:text-[1.375rem]">{route!.name}</h1>
                    {typeCfg && (
                      <Badge variant={typeCfg.variant} className="text-[0.625rem] uppercase">
                        {typeCfg.label}
                      </Badge>
                    )}
                  </div>
                  {route!.description && (
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{route!.description}</p>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="mr-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <CheckIcon className="size-3.5 text-shallow-500" />
              Saved
            </span>
            <div className="h-5 w-px bg-border" />
            <Button variant="outline" size="sm">
              <UploadIcon data-icon="inline-start" />
              Import GPX
            </Button>
            <Button variant="outline" size="sm">
              <DownloadIcon data-icon="inline-start" />
              Export GPX
            </Button>
            <Button variant="outline" size="sm">
              Duplicate
            </Button>
            <Button>Assign to Expedition</Button>
          </div>
        </div>
      </motion.div>

      {/* ── Passage summary strip (above map) ── */}
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.01 }}>
        <Card>
          <CardContent className="py-1.5 px-0">
            <div className="flex items-center">
              {isPending
                ? (["nm", "duration", "speed", "waypoints"] as const).map((k) => (
                    <div key={k} className="flex flex-1 items-center justify-center px-4">
                      <Skeleton className="h-4 w-20" />
                    </div>
                  ))
                : [
                    {
                      value: route!.totalDistance.toLocaleString(),
                      label: "nm total",
                      color: "text-abyss-500",
                      Icon: Navigation2Icon,
                    },
                    {
                      value: route!.estimatedDuration,
                      label: "est. duration",
                      color: "text-shallow-600",
                      Icon: CompassIcon,
                    },
                    {
                      value: String(route!.averageSpeed),
                      label: "avg kn",
                      color: "text-foreground",
                      Icon: SailboatIcon,
                    },
                    {
                      value: String(route!.waypoints.length),
                      label: "waypoints",
                      color: "text-foreground",
                      Icon: MapPinIcon,
                    },
                  ].map((stat, i, arr) => (
                    <div key={stat.label} className="contents">
                      <div className="flex flex-1 items-center justify-center gap-2 px-4">
                        <stat.Icon className={cn("size-3.5 shrink-0", stat.color)} />
                        <span className={cn("font-heading text-sm font-bold tabular-nums", stat.color)}>
                          {stat.value}
                        </span>
                        <span className="text-xs text-muted-foreground">{stat.label}</span>
                      </div>
                      {i < arr.length - 1 && <div className="h-6 w-px bg-border" />}
                    </div>
                  ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* ── Full-bleed map with overlays — the hero ── */}
      <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.02 }} className="min-h-0 flex-1">
        <div className="relative h-full overflow-hidden rounded-xl">
          <BowfastMap
            center={
              route?.mapRoute[0]
                ? [
                    route.mapRoute[Math.floor(route.mapRoute.length / 2)]![0],
                    route.mapRoute[Math.floor(route.mapRoute.length / 2)]![1],
                  ]
                : [15, 43]
            }
            zoom={7}
            showSeamarks={seamarkOn}
            {...(routeOn && route && mapWaypoints ? { route: route.mapRoute, waypoints: mapWaypoints } : {})}
            height="100%"
          />

          {/* Map toolbar — top left: layers + sheet triggers */}
          <div className="absolute top-4 left-4 z-10 flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setSeamarkOn((v) => !v)}
              className={cn(
                "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold backdrop-blur-sm transition-colors",
                seamarkOn
                  ? "border-abyss-400/40 bg-abyss-500 text-white"
                  : "border-white/10 bg-black/50 text-white/60 hover:text-white/80",
              )}
            >
              <LayersIcon className="size-3" />
              SeaMarks
            </button>
            <button
              type="button"
              onClick={() => setRouteOn((v) => !v)}
              className={cn(
                "flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold backdrop-blur-sm transition-colors",
                routeOn
                  ? "border-abyss-400/40 bg-abyss-500 text-white"
                  : "border-white/10 bg-black/50 text-white/60 hover:text-white/80",
              )}
            >
              <Navigation2Icon className="size-3" />
              Route
            </button>

            {/* Separator */}
            <div className="mx-1 h-5 w-px bg-white/15" />

            {/* Passage Notes sheet */}
            <Sheet>
              <SheetTrigger
                render={
                  <button
                    type="button"
                    className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-black/50 px-3 py-1.5 text-xs font-semibold text-white/60 backdrop-blur-sm transition-colors hover:text-white/80"
                  >
                    <FileTextIcon className="size-3" />
                    Notes
                  </button>
                }
              />
              <SheetContent side="right" className="w-[420px] sm:max-w-[420px]">
                <SheetHeader>
                  <SheetTitle className="font-heading">Passage Notes</SheetTitle>
                  <SheetDescription>Tactical notes, weather windows, hazards to avoid</SheetDescription>
                </SheetHeader>
                <div className="px-4">
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Tactical notes, weather windows, hazards to avoid…"
                    rows={12}
                    className="text-sm leading-relaxed"
                  />
                </div>
              </SheetContent>
            </Sheet>

            {/* Linked Expeditions sheet */}
            {/* TODO: Expedition count not available until schema adds join — always shown for now */}
            <Sheet>
              <SheetTrigger
                render={
                  <button
                    type="button"
                    className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-black/50 px-3 py-1.5 text-xs font-semibold text-white/60 backdrop-blur-sm transition-colors hover:text-white/80"
                  >
                    <SailboatIcon className="size-3" />
                    Expeditions
                  </button>
                }
              />
              <SheetContent side="right" className="w-[420px] sm:max-w-[420px]">
                <SheetHeader>
                  <SheetTitle className="font-heading">Linked Expeditions</SheetTitle>
                  <SheetDescription>Expeditions using this route</SheetDescription>
                </SheetHeader>
                {/* TODO: Linked expeditions not in RouteDetail schema — needs schema join */}
                <div className="px-4">
                  <p className="text-sm text-muted-foreground">No expedition data available yet.</p>
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Legend — bottom left */}
          <div className="absolute bottom-4 left-4 z-10 rounded-xl border border-white/10 bg-[#0d1b2a]/88 px-3.5 py-2.5 backdrop-blur-md">
            <p className="mb-1.5 text-[0.625rem] font-bold uppercase tracking-[0.1em] text-white/50">Legend</p>
            {LEGEND_ITEMS.map((item) => (
              <div key={item.kind} className="flex items-center gap-2 py-0.5">
                <span className="inline-block size-2.5 rounded-full" style={{ background: item.color }} />
                <span className="text-xs text-white/70">{item.label}</span>
              </div>
            ))}
            <div className="mt-1.5 flex items-center gap-2 border-t border-white/10 pt-1.5">
              <div className="w-5 border-b-2 border-dashed border-[#047cc7] opacity-80" />
              <span className="text-xs text-white/70">Route track</span>
            </div>
          </div>

          {/* Floating waypoint panel — right side */}
          <div className="absolute top-4 right-4 bottom-4 z-10 hidden w-[340px] flex-col overflow-hidden rounded-xl border border-white/10 bg-[#0d1b2a]/90 shadow-2xl backdrop-blur-xl lg:flex">
            <div className="shrink-0 border-b border-white/10 px-4 pt-4 pb-3">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-sm font-semibold text-white/90">Waypoints</h2>
                {route && (
                  <Badge variant="planning" className="text-[0.5625rem]">
                    {route.waypoints.length} pts
                  </Badge>
                )}
              </div>
              {route && (
                <div className="mt-3">
                  <OverlayPassageStats
                    totalDistance={route.totalDistance}
                    estimatedDuration={route.estimatedDuration}
                    averageSpeed={route.averageSpeed}
                  />
                </div>
              )}
            </div>
            <ScrollArea className="flex-1 min-h-0">
              <div className="px-4 py-2">{route && <OverlayWaypointList waypoints={route.waypoints} />}</div>
            </ScrollArea>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
